// ── Types ──────────────────────────────────────────────────
export type DietType =
  | "Fat Loss"
  | "Weight Gain"
  | "Hypertrophy"
  | "Texture Modified"
  | "Therapeutic";

export const DIET_TYPES: DietType[] = [
  "Fat Loss",
  "Weight Gain",
  "Hypertrophy",
  "Texture Modified",
  "Therapeutic",
];

export type MealOption = {
  id: number;
  name: string;
  quantity: string;   // e.g. "200g", "1 cup", "2 eggs"
  cal: number;
  protein: number;
  fat: number;
  carb: number;
};

export type FoodLibraryItem = {
  id: string;
  name: string;
  quantity: string;
  unit?: string;
  category?: string;
  cal: number;
  protein: number;
  fat: number;
  carb: number;
};

export type MealSection = {
  id: string;
  type: MealKey;
  items: MealOption[];
};

export type DietHistoryEntry = {
  id: string;
  dietType: DietType;
  planWeeks: 1|2|3|4;
  weeks: Record<string, { sections: MealSection[] }>;
  completedAt?: { _seconds: number };
};

export type WeekDietPlan   = { sections: MealSection[] };
export type ClientDietPlan = Record<string, WeekDietPlan>;

export type MeasurementRecord = {
  id?: string;
  date: string;
  weight?: number;
  height?: number;
  bodyFatPct?: number;
  musclePct?: number;
  waterPct?: number;
  idealWeight?: number;
  waist?: number;
  arm?: number;
  wrist?: number;
  hips?: number;
  fatClipper?: number;
  visceralFat?: number;
};

export type WeightPoint = { date: string; kg: number };

export type Client = {
  id: string;
  name: string;
  initials: string;
  email: string;
  status: "active" | "inactive" | "pending";
  // Fields filled in by dietitian — optional for self-registered (pending) clients
  age?: number;
  gender?: "M" | "F";
  phone?: string;
  height?: number;
  goal?: string;
  startDate?: string;
  startWeight?: number;
  currentWeight?: number;
  idealWeight?: number;
  bodyFatPct?: number;
  musclePct?: number;
  waterPct?: number;
  nextApt: string | null;
  nextAptType: string;
  unlockedWeeks: string[];
  activePlanWeeks?: number;
  dietType?: DietType;
  planWeeks?: 1 | 2 | 3 | 4;
  weightHistory: WeightPoint[];
  measurements: MeasurementRecord[];
  dietPlan: ClientDietPlan;
  notes?: string;
};

// ── Helpers ────────────────────────────────────────────────
export const WEEKS      = ["Week 1", "Week 2", "Week 3", "Week 4"] as const;
export const MEAL_KEYS  = ["Breakfast", "Snack", "Lunch", "Dinner", "Supplement"] as const;
export type  MealKey    = typeof MEAL_KEYS[number];
export const MEAL_META: Record<MealKey, { emoji: string }> = {
  Breakfast:  { emoji: "🥣" },
  Snack:      { emoji: "🍎" },
  Lunch:      { emoji: "🍗" },
  Dinner:     { emoji: "🥗" },
  Supplement: { emoji: "💊" },
};

let _id = 1;
const opt = (name: string, quantity: string, cal: number, protein: number, fat: number, carb: number): MealOption =>
  ({ id: _id++, name, quantity, cal, protein, fat, carb });



// ── Today's schedule ───────────────────────────────────────
export type AptStatus = "upcoming" | "break" | "open" | "done";
export type TodayApt = { time: string; client: string; type: string; duration: number; status: AptStatus };
